<template>
  <section
      class="banner-hero"
      :class="{ 'is-compact': compact }"
      @pointerdown="onPointerDown"
      @pointerup="onPointerUp"
      @pointercancel="resetPointer"
  >
    <div class="banner-frame" aria-hidden="true">
      <div
          v-for="(img, i) in images"
          :key="img"
          class="banner-slide"
          :class="{ 'is-active': i === currentIndex }"
          :style="{ backgroundImage: `url(${img})` }"
      />
    </div>

    <div class="hero-content text-center">
      <h1 class="main-title"><span class="color-primary">State</span>MC</h1>
      <p class="sub-title">{{ $t('banner.subtitle') }}</p>
      <v-btn
          class="hero-button"
          color="primary"
          size="large"
          prepend-icon="mdi-controller-classic-outline"
          elevation="3"
          @click="$router.push({ name: 'Join', hash: '#join' })"
      >
        {{ $t('join.button') }}
      </v-btn>
    </div>

    <div class="banner-dots" aria-label="Banner Bilder">
      <button
          v-for="(img, i) in images"
          :key="`dot-${img}`"
          class="banner-dot"
          :class="{ 'is-active': i === currentIndex }"
          type="button"
          :aria-label="`Bild ${i + 1}`"
          @click="setSlide(i)"
      />
    </div>
  </section>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { Utils } from '@/modules/utils';

defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
});

const images = Utils.getBannerImages();
const currentIndex = ref(0);
const pointerStartX = ref(null);
let interval;

const setSlide = (index) => {
  currentIndex.value = index;
  restartCycle();
};

const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % images.length;
};

const previousSlide = () => {
  currentIndex.value = (currentIndex.value - 1 + images.length) % images.length;
};

const startCycle = () => {
  if (images.length <= 1) return;
  interval = window.setInterval(nextSlide, 6000);
};

const stopCycle = () => {
  if (interval) {
    window.clearInterval(interval);
    interval = undefined;
  }
};

const restartCycle = () => {
  stopCycle();
  startCycle();
};

const onPointerDown = (event) => {
  pointerStartX.value = event.clientX;
};

const onPointerUp = (event) => {
  if (pointerStartX.value === null) return;

  const distance = event.clientX - pointerStartX.value;
  resetPointer();

  if (Math.abs(distance) < 42) return;

  if (distance > 0) {
    previousSlide();
  } else {
    nextSlide();
  }

  restartCycle();
};

const resetPointer = () => {
  pointerStartX.value = null;
};

onMounted(startCycle);
onBeforeUnmount(stopCycle);
</script>

<style scoped>
.banner-hero {
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: clamp(210px, 24vw, 320px);
  overflow: hidden;
  background: rgb(var(--v-theme-site-chrome));
  touch-action: pan-y;
  user-select: none;
}

.banner-frame,
.banner-slide {
  position: absolute;
  inset: 0;
}

.banner-frame::after {
  position: absolute;
  inset: 0;
  content: "";
  background:
    linear-gradient(90deg, rgba(16, 19, 20, 0.82), rgba(16, 19, 20, 0.2) 48%, rgba(16, 19, 20, 0.76)),
    linear-gradient(180deg, rgba(16, 19, 20, 0.24), rgba(16, 19, 20, 0.72));
}

.banner-slide {
  background-position: center;
  background-size: cover;
  opacity: 0;
  transform: scale(1.04);
  transition: opacity 850ms ease, transform 6000ms ease;
  will-change: opacity, transform;
}

.banner-slide.is-active {
  opacity: 1;
  transform: scale(1);
}

.hero-content {
  position: relative;
  z-index: 1;
  width: min(92%, 720px);
  font-family: 'Poppins', sans-serif;
  font-weight: bold;
  text-shadow: 0 3px 18px rgba(0, 0, 0, 0.72);
}

.main-title {
  margin: 0;
  color: white;
  font-size: clamp(38px, 6vw, 72px);
  line-height: 0.95;
}

.sub-title {
  margin: 12px 0 24px;
  color: rgba(255, 255, 255, 0.88);
  font-size: clamp(18px, 2.6vw, 32px);
  line-height: 1.15;
}

.hero-button {
  min-height: 48px;
  padding: 0 24px !important;
  border-radius: 8px !important;
  font-weight: 800;
  letter-spacing: 0;
}

.banner-dots {
  position: absolute;
  right: 24px;
  bottom: 18px;
  z-index: 1;
  display: flex;
  gap: 8px;
}

.banner-dot {
  width: 28px;
  height: 4px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.36);
  cursor: pointer;
  transition: width 180ms ease, background 180ms ease;
}

.banner-dot.is-active {
  width: 42px;
  background: rgb(var(--v-theme-primary));
}

.banner-hero.is-compact {
  min-height: clamp(125px, 14vw, 170px);
}

.banner-hero.is-compact .main-title {
  font-size: clamp(30px, 3.4vw, 42px);
}

.banner-hero.is-compact .sub-title {
  margin: 8px 0 0;
  font-size: clamp(15px, 1.5vw, 18px);
}

.banner-hero.is-compact .hero-button {
  display: none;
}

.banner-hero.is-compact .banner-dots {
  bottom: 12px;
}

@media screen and (max-width: 800px) {
  .banner-hero {
    min-height: 195px;
  }

  .sub-title {
    margin-bottom: 18px;
  }

  .hero-button {
    min-height: 42px;
    padding: 0 16px !important;
  }

  .banner-hero.is-compact {
    min-height: 120px;
  }
}

@media screen and (max-width: 500px) {
  .banner-hero {
    min-height: 175px;
  }

  .main-title {
    font-size: 34px;
  }

  .sub-title {
    margin: 8px 0 14px;
    font-size: 16px;
  }

  .hero-button {
    min-height: 38px;
    padding: 0 12px !important;
    font-size: 13px;
  }

  .banner-dots {
    right: 14px;
    bottom: 12px;
    gap: 6px;
  }

  .banner-dot {
    width: 18px;
  }

  .banner-dot.is-active {
    width: 28px;
  }

  .banner-hero.is-compact {
    min-height: 108px;
  }

  .banner-hero.is-compact .main-title {
    font-size: 28px;
  }

  .banner-hero.is-compact .sub-title {
    display: none;
  }
}
</style>
